// What a sheet's setup is — sort, filters, group by, hidden columns, widths —
// how its active filters read as chips, and the views saved in this browser
// (Leon, 2026-10-23). The chips and the reducer-like helpers are pure; the
// storage functions swallow a blocked localStorage, so nothing breaks in a
// private window — the setup just isn't remembered.

import type { SheetCondition, SheetSort } from "./sheet-table.model";

export interface SheetView {
  sort: SheetSort | null;
  hidden: Record<string, string[]>;
  conditions: Record<string, SheetCondition>;
  groupBy: string | null;
  hiddenColumns: string[];
  widths: Record<string, number>;
  /** "Show only these": the row ids kept, or null. */
  only: string[] | null;
}

export const EMPTY_SHEET_VIEW: SheetView = {
  sort: null,
  hidden: {},
  conditions: {},
  groupBy: null,
  hiddenColumns: [],
  widths: {},
  only: null,
};

/** Fills in fields a view saved by an older build doesn't have. */
export function normalizeView(view: Partial<SheetView> | null | undefined): SheetView {
  return { ...EMPTY_SHEET_VIEW, ...(view ?? {}) };
}

export function isFiltered(view: SheetView): boolean {
  return (
    Object.values(view.hidden).some((values) => values.length > 0) ||
    Object.keys(view.conditions).length > 0 ||
    view.only !== null
  );
}

/** Clears every filter, keeping sort, grouping and columns. */
export function withoutFilters(view: SheetView): SheetView {
  return { ...view, hidden: {}, conditions: {}, only: null };
}

export interface FilterChip {
  key: string;
  label: string;
}

export const PRESET_LABEL = { today: "Today", week: "This week", month: "This month" } as const;

/** The chip for "Show only these". */
export const ONLY_CHIP = "__only";

const peso = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });

export function conditionLabel(condition: SheetCondition, money: boolean): string {
  const number = (value: number) => (money ? peso.format(value) : String(value));
  switch (condition.op) {
    case "contains":
      return `contains “${condition.value}”`;
    case "equals":
      return `is “${condition.value}”`;
    case "gt":
      return `> ${number(condition.value)}`;
    case "lt":
      return `< ${number(condition.value)}`;
    case "between":
      return `${number(condition.from)} – ${number(condition.to)}`;
    case "empty":
      return "is empty";
    case "not_empty":
      return "is not empty";
    case "date":
      if (condition.preset) return PRESET_LABEL[condition.preset];
      return `${condition.from || "…"} – ${condition.to || "…"}`;
  }
}

/**
 * One chip per filtered column: "Payment: Cash, GiftPass" while two or fewer
 * values remain, "Payment: 3 of 5" after that, then any condition.
 */
export function filterChips(
  view: SheetView,
  columns: readonly { key: string; label: string; money?: boolean }[],
  valuesOf: (key: string) => readonly { value: string; label: string }[],
): FilterChip[] {
  const chips: FilterChip[] = [];
  if (view.only) {
    const count = view.only.length;
    chips.push({
      key: ONLY_CHIP,
      label: `Only the ${count} selected ${count === 1 ? "row" : "rows"}`,
    });
  }
  for (const column of columns) {
    const hidden = new Set(view.hidden[column.key] ?? []);
    const condition = view.conditions[column.key];
    const parts: string[] = [];
    if (hidden.size > 0) {
      const all = valuesOf(column.key);
      const kept = all.filter((value) => !hidden.has(value.value));
      parts.push(
        kept.length === 0
          ? "nothing"
          : kept.length <= 2
            ? kept.map((value) => value.label).join(", ")
            : `${kept.length} of ${all.length}`,
      );
    }
    if (condition) parts.push(conditionLabel(condition, column.money === true));
    if (parts.length > 0)
      chips.push({ key: column.key, label: `${column.label}: ${parts.join(" · ")}` });
  }
  return chips;
}

// ── remembered per browser ─────────────────────────────────────────────────

export interface SavedView {
  name: string;
  view: SheetView;
}

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private window or blocked storage: not remembered, nothing breaks.
  }
}

export const sheetStorage = {
  loadCurrent: (scope: string) => normalizeView(read<Partial<SheetView>>(`${scope}.current`)),
  saveCurrent: (scope: string, view: SheetView) => write(`${scope}.current`, view),
  loadSaved: (scope: string): SavedView[] =>
    (read<SavedView[]>(`${scope}.views`) ?? []).map((saved) => ({
      ...saved,
      view: normalizeView(saved.view),
    })),
  saveSaved: (scope: string, views: SavedView[]) => write(`${scope}.views`, views),
};
